
import { useQuery } from '@tanstack/react-query';
import { useAuth } from '../contexts/AuthContext';
import { apiRequest } from '../lib/api';
import { Reminder, ReminderRecord } from '../types/reminders';
import { useMemo } from 'react';

async function fetchAllReminders(accessToken: string | null | undefined) {
  return apiRequest<ReminderRecord[]>('/api/reminders', { method: 'GET' }, accessToken);
}

export function useAllReminders() {
  const { session } = useAuth();

  const { data: remindersData, isLoading } = useQuery<ReminderRecord[]>({
    queryKey: ['reminders'],
    enabled: Boolean(session),
    queryFn: () => fetchAllReminders(session?.access_token),
  });

  const reminders = useMemo(() => {
    if (!remindersData) return { today: [], upcoming: [], overdue: [] };

    const now = new Date();
    const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
    const tomorrow = new Date(today);
    tomorrow.setDate(tomorrow.getDate() + 1);

    const categorizedReminders = remindersData.reduce(
      (acc, reminder) => {
        // Skip reminders that are not pending
        if (reminder.status !== 'pending') {
          return acc;
        }

        const dueDate = new Date(reminder.due_at);
        let category: 'today' | 'upcoming' | 'overdue';

        if (dueDate < today) {
          category = 'overdue';
        } else if (dueDate >= today && dueDate < tomorrow) {
          category = 'today';
        } else {
          category = 'upcoming';
        }

        const formattedReminder: Reminder = {
          id: reminder.id,
          clientName: reminder.clients.name,
          clientId: reminder.clients.id,
          clientStatus: reminder.clients.status,
          message: reminder.title,
          dueDate: dueDate.toLocaleDateString(undefined, { month: 'short', day: 'numeric' }),
          dueTime: dueDate.toLocaleTimeString(undefined, { hour: '2-digit', minute: '2-digit' }),
          dueAt: reminder.due_at, // Preserve original ISO timestamp
          category,
          clientWhatsappJid: reminder.clients.whatsapp_jid,
          is_recurring_instance: reminder.is_recurring_instance,
          recurring_template_id: reminder.recurring_template_id,
          plan_id: reminder.plan_id,
          plan_title: reminder.plans?.title || null,
        };

        acc[category].push(formattedReminder);
        return acc;
      },
      { today: [], upcoming: [], overdue: [] } as { [key in 'today' | 'upcoming' | 'overdue']: Reminder[] }
    );

    return categorizedReminders;
  }, [remindersData]);

  return { reminders, isLoading };
}
